import EmailIcon from '@mui/icons-material/Email'
import InfoIcon from '@mui/icons-material/Info'
import KeyIcon from '@mui/icons-material/Key'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { Alert, Box, Button, CircularProgress, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material'
import Divider from '@mui/material/Divider'
import { useSetAtom } from 'jotai'
import * as React from 'react'
import { useLocation, useNavigate } from 'react-router'

import { loginCommandAtom } from '../../domains/auth/AuthCommand'
import AuthService from '../../services/AuthService'
import useBar from '../common/Bar'
import SocialLoginButtons from '../common/SocialLoginButtons'
import ConfirmationDialog from '../dialog/ConfirmationDialog'

export default function LoginForm({ successMessage = '', afterLogin = () => {} }) {
  const navigate = useNavigate()
  const location = useLocation()
  const loginCommand = useSetAtom(loginCommandAtom)

  const bar = useBar()
  const showDemoInfo = false

  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [showDemo, setShowDemo] = React.useState(showDemoInfo)
  const [error, setError] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [forgetDialogOpen, setForgetDialogOpen] = React.useState(false)
  const [resetEmail, setResetEmail] = React.useState('')
  const [resetStatus, setResetStatus] = React.useState({ message: '', severity: 'info' })
  const [isResetting, setIsResetting] = React.useState(false)
  const [showPassword, setShowPassword] = React.useState(false)

  const [message, setMessage] = React.useState('')

  React.useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message || successMessage)
    }
  }, [location.state?.message, successMessage])

  const navigateAfterLogin = () => {
    if (afterLogin) {
      afterLogin()
    } else if (location.state?.redirectTo) {
      navigate(location.state.redirectTo)
    } else {
      navigate('/')
    }
  }

  const handleForgetPassword = async () => {
    if (!resetEmail) {
      setResetStatus({ message: 'Please enter your email address', severity: 'error' })
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(resetEmail)) {
      setResetStatus({ message: 'Please enter a valid email address', severity: 'error' })
      return
    }

    setIsResetting(true)
    setResetStatus({ message: '', severity: 'info' })

    try {
      await AuthService.forgetPassword(resetEmail)
      setResetStatus({
        message: 'Password reset instructions have been sent to your email',
        severity: 'success',
      })
      bar.success('Password reset instructions have been sent to your email if the email is registered')
      setForgetDialogOpen(false)
      setResetEmail('')
      setResetStatus({ message: '', severity: 'info' })
    } catch (error) {
      console.error('Password reset error:', error)

      if (error.response) {
        // Handle different error status codes
        setResetStatus({
          message: 'Failed to reset password. Please try again later',
          severity: 'error',
        })
      } else if (error.request) {
        // Network error
        setResetStatus({
          message: 'Network error. Please check your connection and try again',
          severity: 'error',
        })
      } else {
        // Something happened in setting up the request
        setResetStatus({
          message: 'An unexpected error occurred. Please try again later',
          severity: 'error',
        })
      }
    } finally {
      setIsResetting(false)
    }
  }

  // Clear messages when user starts typing
  const handleInputChange = (e, setter) => {
    setError('')
    setter(e.target.value)
  }

  const handleSignUp = (e) => {
    e.preventDefault()
    navigate('/signup', {
      state: {
        redirectTo: location.state?.redirectTo,
      },
    })
  }

  const handleLoginSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    try {
      // await login(email, password)
      await loginCommand({ email, password })
      navigateAfterLogin()
    } catch (error) {
      if (error.response) {
        switch (error.response.status) {
          case 401:
            setError('Invalid email or password')
            break
          case 429:
            setError('Too many login attempts. Please try again later')
            break
          case 404:
            setError('Account not found. Please check your email')
            break
          default:
            setError('Login failed. Please try again')
        }
      } else if (error.request) {
        setError('Network error. Please check your connection')
      } else {
        setError('Something went wrong. Please try again')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box>
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showDemo && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Demo credentials:</Typography>

            {/* Disable for security reasons */}
            {/* <Typography component="div">
              <strong>Admin:</strong> admin@skiclub.com / admin123
            </Typography> */}

            <Box>
              <Typography component="div">
                <strong>Instructors:</strong>
              </Typography>
              <Stack spacing={0.5} sx={{ pl: 2 }}>
                <Typography component="div">• snowboard1@skiclub.com / instructor123</Typography>
                <Typography component="div">• ski1@skiclub.com / instructor123</Typography>
                <Typography component="div">• dual1@skiclub.com / instructor123</Typography>
              </Stack>
            </Box>

            <Box>
              <Typography component="div">
                <strong>Members:</strong>
              </Typography>
              <Stack spacing={0.5} sx={{ pl: 2 }}>
                <Typography component="div">• alice@example.com / member123</Typography>
                <Typography component="div">• bob@example.com / member123</Typography>
              </Stack>
            </Box>
          </Stack>
        </Alert>
      )}

      <form onSubmit={handleLoginSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => handleInputChange(e, setEmail)}
          required
          disabled={isSubmitting}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={(e) => handleInputChange(e, setPassword)}
          required
          disabled={isSubmitting}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <KeyIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {showPassword ? (
                  <VisibilityOffIcon
                    fontSize="small"
                    sx={{
                      cursor: isSubmitting ? 'default' : 'pointer',
                      color: isSubmitting ? 'text.disabled' : 'action.active',
                      '&:hover': {
                        color: isSubmitting ? 'text.disabled' : 'primary.main',
                      },
                    }}
                    onClick={() => !isSubmitting && setShowPassword(false)}
                  />
                ) : (
                  <VisibilityIcon
                    fontSize="small"
                    sx={{
                      cursor: isSubmitting ? 'default' : 'pointer',
                      color: isSubmitting ? 'text.disabled' : 'action.active',
                      '&:hover': {
                        color: isSubmitting ? 'text.disabled' : 'primary.main',
                      },
                    }}
                    onClick={() => !isSubmitting && setShowPassword(true)}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 2, mb: 1 }}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSubmitting ? 'Signing in...' : 'Sign In'}
        </Button>

        {showDemoInfo && (
          <Button fullWidth variant="text" size="small" onClick={() => setShowDemo(!showDemo)}>
            {showDemo ? 'Hide Demo Info' : 'Show Demo Info'}
          </Button>
        )}
      </form>

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary" component="div">
          <Link href="#" underline="hover" onClick={() => setForgetDialogOpen(true)}>
            Forgot password?
          </Link>
        </Typography>
      </Box>

      <Divider sx={{ my: 2 }}>or</Divider>

      <SocialLoginButtons mode="login" onSetError={setError} />

      <Typography variant="body2" color="text.secondary" component="div">
        Don't have an account?{' '}
        <Link
          component="button"
          variant="body2"
          onClick={handleSignUp}
          sx={{
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Sign up
        </Link>
      </Typography>

      {/* Password Reset Dialog */}
      <ConfirmationDialog
        open={forgetDialogOpen}
        onClose={() => !isResetting && setForgetDialogOpen(false)}
        onConfirm={handleForgetPassword}
        title="Forgot Password"
        content=""
        actions={{
          cancel: {
            label: 'Cancel',
            onClick: () => !isResetting && setForgetDialogOpen(false),
          },
          confirm: {
            label: 'Send Reset Link',
            onClick: handleForgetPassword,
            submitting: isResetting,
          },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your email address and we'll send you instructions to reset your password.
        </Typography>
        {resetStatus.message && (
          <Alert severity={resetStatus.severity} sx={{ mb: 2 }}>
            {resetStatus.message}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Email"
          type="email"
          autoComplete="email"
          value={resetEmail}
          onChange={(e) => setResetEmail(e.target.value)}
          required
          disabled={isResetting}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
        />
      </ConfirmationDialog>
    </Box>
  )
}
